import { useTranslation } from "react-i18next";
import { Navigation } from "@/components/sections/Navigation";
import { useState, useEffect } from "react";
import { Calendar, MapPin, Users, ArrowLeft, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { ImportedEventsByCategory } from "@/components/sections/ImportedEventsByCategory";
import { motion } from "framer-motion";

const seasons = [
  {
    id: 2,
    name: { ar: "موسم الرياض", en: "Riyadh Season" },
    date: { ar: "12 أكتوبر 2024 - 12 مارس 2025", en: "October 12, 2024 - March 12, 2025" },
    location: { ar: "الرياض", en: "Riyadh" },
    description: {
      ar: "أكبر موسم ترفيهي في المملكة يضم مناطق ترفيه متعددة وفعاليات رياضية وثقافية",
      en: "The largest entertainment season in the Kingdom featuring multiple entertainment zones and sports and cultural events"
    },
    image: "/lovable-uploads/c8a0e95c-de77-438a-9f09-dfbe421b399e.png",
    expectedVisitors: 500000
  },
  {
    id: 3,
    name: { ar: "موسم الدرعية", en: "Diriyah Season" },
    date: { ar: "20 ديسمبر 2024 - 15 مارس 2025", en: "December 20, 2024 - March 15, 2025" },
    location: { ar: "الدرعية", en: "Diriyah" },
    description: {
      ar: "موسم يجمع بين الأصالة والمعاصرة في موقع تراثي فريد، مع فعاليات رياضية وثقافية",
      en: "A season that combines authenticity and modernity in a unique heritage site, with sports and cultural events"
    },
    image: "/lovable-uploads/1ada5c88-a855-4ad0-b266-a1f5a7e354d1.png",
    expectedVisitors: 150000
  },
  {
    id: 6,
    name: { ar: "موسم النحالين", en: "Beekeepers Season" },
    date: { ar: "19 فبراير 2025 - 30 أكتوبر 2025", en: "February 19, 2025 - October 30, 2025" },
    location: { ar: "مناطق متعددة", en: "Multiple Regions" },
    description: {
      ar: "موسم متخصص في تربية النحل وإنتاج العسل، يجمع النحالين من مختلف مناطق المملكة",
      en: "A specialized season for beekeeping and honey production, bringing together beekeepers from various regions of the Kingdom"
    },
    image: "/lovable-uploads/f954a6f1-a103-4892-9660-275ddcaba41b.png",
    expectedVisitors: 100000
  },
  {
    id: 7,
    name: { ar: "موسم صيد الروبيان بجازان", en: "Jazan Shrimp Fishing Season" },
    date: { ar: "1 سبتمبر 2025 - 1 أبريل 2025", en: "September 1, 2025 - April 1, 2025" },
    location: { ar: "جازان", en: "Jazan" },
    description: {
      ar: "موسم صيد الروبيان في مياه جازان، يجمع الصيادين المحليين ويعزز السياحة البحرية",
      en: "Shrimp fishing season in Jazan waters, bringing together local fishermen and promoting marine tourism"
    },
    image: "/lovable-uploads/0aeba568-7c0a-4656-b253-c24bee955ee7.png",
    expectedVisitors: 80000
  },
  {
    id: 8,
    name: { ar: "موسم ملح القصب", en: "Melh Al Qasab Season" },
    date: { ar: "20 فبراير 2025 - 27 فبراير 2025", en: "February 20, 2025 - February 27, 2025" },
    location: { ar: "القصب", en: "Al Qasab" },
    description: {
      ar: "موسم خاص بإنتاج الملح التقليدي في منطقة القصب، يعرض التقنيات التقليدية وأهمية الملح في التراث السعودي",
      en: "A season dedicated to traditional salt production in Al Qasab area, showcasing traditional techniques and the importance of salt in Saudi heritage"
    },
    image: "/lovable-uploads/d9076628-287a-4224-b923-7a7835e64ed9.png",
    expectedVisitors: 50000
  }
];

const SaudiSeasons = () => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const navigate = useNavigate();
  const [registrationType, setRegistrationType] = useState("");
  const handleRegistrationClick = () => {};

  return (
    <div className={`min-h-screen flex flex-col bg-gray-100 ${isRTL ? "rtl" : "ltr"}`}>
      <Navigation 
        isRTL={isRTL}
        setRegistrationType={setRegistrationType}
        handleRegistrationClick={handleRegistrationClick}
      />
      <main className="flex-1">
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-8">
              <Button
                onClick={() => navigate('/')}
                variant="ghost"
                className="flex items-center gap-2 hover:bg-gray-200"
              >
                {isRTL ? (
                  <>
                    العودة للرئيسية
                    <ArrowRight className="w-4 h-4" />
                  </>
                ) : (
                  <>
                    <ArrowLeft className="w-4 h-4" />
                    Back to Home
                  </>
                )}
              </Button>
              <h1 className="text-4xl font-bold text-gray-700">
                {isRTL ? "المواسم السعودية" : "Saudi Seasons"}
              </h1>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {seasons.map((season, index) => (
                <motion.div
                  key={season.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  className="relative group"
                >
                  <div 
                    className="rounded-3xl overflow-hidden transition-all duration-500"
                    style={{
                      background: 'linear-gradient(145deg, #f0f0f0, #ffffff)',
                      boxShadow: '8px 8px 20px #d1d1d1, -8px -8px 20px #ffffff'
                    }}
                  >
                    {/* Inner Shadow on Hover */}
                    <div 
                      className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                      style={{
                        boxShadow: 'inset 4px 4px 10px #d1d1d1, inset -4px -4px 10px #ffffff'
                      }}
                    />
                    
                    {/* Image */}
                    <div className="relative h-52 overflow-hidden m-3 rounded-2xl">
                      <img
                        src={season.image}
                        alt={season.name[isRTL ? "ar" : "en"]}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent rounded-2xl" />
                    </div>
                    
                    {/* Content */}
                    <div className="p-5 pt-2">
                      <h3 className="text-xl font-bold mb-4 text-gray-700 group-hover:text-primary transition-colors">
                        {season.name[isRTL ? "ar" : "en"]}
                      </h3>
                      
                      <div className="space-y-3 text-gray-500">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-9 h-9 rounded-xl flex items-center justify-center"
                            style={{
                              background: 'linear-gradient(145deg, #ffffff, #f0f0f0)',
                              boxShadow: '3px 3px 6px #e0e0e0, -3px -3px 6px #ffffff'
                            }}
                          >
                            <Calendar className="w-4 h-4 text-primary" />
                          </div>
                          <span className="text-sm font-medium text-gray-600">{season.date[isRTL ? "ar" : "en"]}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-9 h-9 rounded-xl flex items-center justify-center"
                            style={{
                              background: 'linear-gradient(145deg, #ffffff, #f0f0f0)',
                              boxShadow: '3px 3px 6px #e0e0e0, -3px -3px 6px #ffffff'
                            }}
                          >
                            <MapPin className="w-4 h-4 text-emerald-500" />
                          </div>
                          <span className="text-sm font-medium text-gray-600">{season.location[isRTL ? "ar" : "en"]}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-9 h-9 rounded-xl flex items-center justify-center"
                            style={{
                              background: 'linear-gradient(145deg, #ffffff, #f0f0f0)',
                              boxShadow: '3px 3px 6px #e0e0e0, -3px -3px 6px #ffffff'
                            }}
                          >
                            <Users className="w-4 h-4 text-blue-500" />
                          </div>
                          <span className="text-sm font-medium text-gray-600">
                            {isRTL ? "الزوار المتوقعون: " : "Expected Visitors: "}
                            {season.expectedVisitors.toLocaleString()}
                          </span>
                        </div>
                      </div>
                      
                      <p className="mt-4 text-sm text-gray-400 line-clamp-2">
                        {season.description[isRTL ? "ar" : "en"]}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
        
        {/* Imported Events Section */}
        <ImportedEventsByCategory 
          isRTL={isRTL}
          category="seasons"
          title={{ ar: "فعاليات المواسم", en: "Seasons Events" }}
        />
      </main>
      <footer className="w-full bg-white border-t mt-auto">
        <div className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <p className="text-center text-gray-600 text-sm">
            {isRTL 
              ? "جميع الحقوق محفوظة لـ منصة فعاليات الخليج © ٢٠٢٦" 
              : "All rights reserved to Gulf Events Platform © 2026"}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default SaudiSeasons;
